import { createAsyncThunk } from '@reduxjs/toolkit';
import historyManager from './historyManager';
import { createSnapshot } from './snapshotSerializer';
import reconcileInPlace from './restoreOrchestrator';
import { recipeCache } from '@kemu-io/kemu-core/common';
import { widgetMapToStatelessMap } from '@common/utils';
import { setGates } from '@src/features/LogicMapper/logicMapperSlice';
import portsRepaintRegistry from '@common/portsRepaintRegistry';
import canvasViewportRegistry from '@common/canvasViewportRegistry';
import historyToggle from '@src/app/reducers/history/historyToggle';

export const undoHistoryAction = createAsyncThunk('/history/undo', async (_: void, thunkAPI): Promise<void> => {
  const state = thunkAPI.getState() as any;
  const recipePoolId = state.workspace.currentRecipe.poolId as string | null;
  if (!recipePoolId) { return; }
  if(!historyToggle.isHistoryEnabled()) { return; }

  const target = historyManager.undo();
  if (!target) { 
    console.log('Nothing to undo');
    return; 
  }

  let revalidateByThing: Record<string, string[]> = {};
  try {
    historyManager.setRestoring(true);
    const res = await reconcileInPlace({ recipePoolId, snapshot: target });
    revalidateByThing = res.revalidateByThing || {};
  } finally {
    historyManager.setRestoring(false);
  }

  // Refresh stateless mirror for the currently open thing
  const selectedBlock = state.workspace.selectedBlockInfo;
  if (selectedBlock) {
    const thing = recipeCache.findThingInRecipe(recipePoolId, selectedBlock.recipeId);
    if (thing) {
      thunkAPI.dispatch(setGates(widgetMapToStatelessMap(thing.gates)));
      const widgetIds = revalidateByThing[selectedBlock.recipeId];
      // Restore canvas zoom/pan if present in snapshot UI context
      const ui = target.uiContext;
      if (ui?.canvasZoom) {
        // We only have zoom in snapshot; pan is derived from defaultPosition
        canvasViewportRegistry.setViewport(recipePoolId, selectedBlock.recipeId, { 
          zoom: ui.canvasZoom,
          x: ui.canvasPosition?.x ?? 0,
          y: ui.canvasPosition?.y ?? 0,
        });
      }
      if (widgetIds && widgetIds.length) {
        // Targeted revalidate for moved/resized widgets ensures endpoint anchors update
        requestAnimationFrame(() => {
          portsRepaintRegistry.repaint(recipePoolId, selectedBlock.recipeId, { widgetIds });
        });
      } else {
        // Fallback to rebuild + repaint if we have no specific ids
        requestAnimationFrame(() => {
          portsRepaintRegistry.repaint(recipePoolId, selectedBlock.recipeId, { rebuildConnections: true });
          requestAnimationFrame(() => {
            portsRepaintRegistry.repaint(recipePoolId, selectedBlock.recipeId);
          });
        });
      }
    }
  }
});

export const redoHistoryAction = createAsyncThunk('/history/redo', async (_: void, thunkAPI): Promise<void> => {
  const state = thunkAPI.getState() as any;
  const recipePoolId = state.workspace.currentRecipe.poolId as string | null;
  console.log('redoHistoryAction', recipePoolId);
  if (!recipePoolId) { return; }
  if(!historyToggle.isHistoryEnabled()) { return; }
  
  const target = historyManager.redo();
  if (!target) { return; }

  let revalidateByThing: Record<string, string[]> = {};
  try {
    historyManager.setRestoring(true);
    const res = await reconcileInPlace({ recipePoolId, snapshot: target });
    revalidateByThing = res.revalidateByThing || {};
  } finally {
    historyManager.setRestoring(false);
  }

  // Refresh stateless mirror for the currently open thing
  const selectedBlock = state.workspace.selectedBlockInfo;
  if (selectedBlock) {
    const thing = recipeCache.findThingInRecipe(recipePoolId, selectedBlock.recipeId);
    if (thing) {
      thunkAPI.dispatch(setGates(widgetMapToStatelessMap(thing.gates)));
      const widgetIds = revalidateByThing[selectedBlock.recipeId];
      const ui = target.uiContext;
      if (ui && (ui.canvasZoom !== undefined)) {
        canvasViewportRegistry.setViewport(recipePoolId, selectedBlock.recipeId, { zoom: ui.canvasZoom });
      }
      if (widgetIds && widgetIds.length) {
        requestAnimationFrame(() => {
          portsRepaintRegistry.repaint(recipePoolId, selectedBlock.recipeId, { widgetIds });
        });
      } else {
        requestAnimationFrame(() => {
          portsRepaintRegistry.repaint(recipePoolId, selectedBlock.recipeId, { rebuildConnections: true });
          requestAnimationFrame(() => {
            portsRepaintRegistry.repaint(recipePoolId, selectedBlock.recipeId);
          });
        });
      }
    }
  }
});

export const captureSnapshotNow = createAsyncThunk('/history/captureNow', async (_: void, thunkAPI): Promise<void> => {
  const state = thunkAPI.getState() as any;
  const recipePoolId = state.workspace.currentRecipe.poolId as string | null;
  if (!recipePoolId || historyManager.isRestoring()) { return; }

  const snapshot = createSnapshot({ recipePoolId, state });
  historyManager.capture(recipePoolId, snapshot);
});


